import { useState } from 'react';
import {
    IComponentCss,
    VerticalTabsCustom,
    TitlePageH1,
} from '@tap/mui-ui';
import { Box, Grid, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ruLocale from 'date-fns/locale/ru';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { GetIssueStatisticOnWorkersReportParams, IssueStatisticOnWorkersReportResult, docAnalyticsService } from '@tap/data-providers';
import { TabComponent, TabLabel, TabsTitle } from '../ReportParameterElements';
import IssueTypeSelect, { IssueDocumentCode } from '@tap/doc-ui/src/components/IssueTypeSelect';
import IssueStatisticOnWorkersReportTable from './IssueStatisticOnWorkersReportTable';
import { SelectDepartmentVerticalTab, useDepartment } from '@tap/tap-ui';
import { NotDataForReportPage } from '../../not-data-for-report/NotDataForReportPage';
import { LoadingButton } from '@mui/lab';
import { ruRU } from '@mui/x-date-pickers/locales';
import { dateTimeHelper } from '@tap/utils';

/*
*   Отчет Статистика по исполнителям для заявок
*/

enum CurrentWindow {
    Options = 'options',
    Chart = 'chart',
    NotData = 'not-data',
}

const css: IComponentCss = {
    titleButton: {
        marginTop: '10px',
        minWidth: '189px',
    },
    contentWrap: {
        padding: '0px',
        marginBottom: '34px',
    },
    title: {
        width: '100%',
        marginBottom: '16px',
    },
    dateRangerGrid: {
        marginBottom: '-8px',
    },
    tableWrap: {
        paddingTop: '5px',
        paddingRight: '8px',
        height: 'calc(100vh - 178px)',
    },
    dateRangePickerStyle: {
        '.MuiMultiInputDateRangeField-separator': {
            display: 'none',
        },
    },
    errorMessageStyle: {
        marginTop: '8px',
        fontSize: '12px',
    },
};

const newDate = new Date();
const initialStartDate = new Date(newDate.setDate(new Date().getDate() - 7));
const minStartDate = new Date(newDate.setDate(new Date().getDate() - 31));


type IssueStatisticOnWorkersReportPageProps = {
    title: string,
}

type IssueStatisticOnWorkersReportPageFormProps = {
    startDate: Date | null,
    endDate: Date | null,
    departmentIds: number[],
    issueType: IssueDocumentCode,
}

export default function IssueStatisticOnWorkersReportPage({ title }: IssueStatisticOnWorkersReportPageProps) {
    const [currentWindow, setCurrentWindow] = useState<CurrentWindow>(CurrentWindow.Options);
    const departmentProps = useDepartment();
    const [reportData, setReportData] = useState<IssueStatisticOnWorkersReportResult | undefined>(undefined);

    // Запрос расчета
    const { values, errors, touched, setFieldValue, ...formik } = useFormik<IssueStatisticOnWorkersReportPageFormProps>({
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            startDate: initialStartDate,
            endDate: new Date(),
            departmentIds: [],
            issueType: IssueDocumentCode.QC,
        },
        validationSchema: Yup.object({
            startDate: Yup.date().required(),
            endDate: Yup.date().required(),
        }),
        onSubmit: (data) => {
            const isAllDepartmentsSelected = data.departmentIds.length === 0 || data.departmentIds.length === departmentProps.sectorList.length;
            const selectedManufactories = departmentProps.manufactoryList.filter(m => m.sectors.filter(s => data.departmentIds.includes(s.id)).length).map(m => m.name);
            const params: GetIssueStatisticOnWorkersReportParams = {
                manufactories: selectedManufactories,
                departmentIds: isAllDepartmentsSelected ? [] : data.departmentIds,
                startFromUtc: new Date(data.startDate!.setHours(0, 0, 0, 0)),
                endToUtc: new Date(data.endDate!.setHours(23, 59, 59, 0)),
                documentCodes: [data.issueType],
            }
            return docAnalyticsService.getIssueStatisticOnWorkersReport(params)
                .then((res) => {
                    if (!res.rows.length) {
                        setCurrentWindow(CurrentWindow.NotData);
                        return;
                    }
                    setReportData(res);
                    setCurrentWindow(CurrentWindow.Chart);
                })
        },
    });

    const handleChangeDateRange = (newPeriod: DateRange<Date>) => {
        formik.setValues({
            ...values,
            startDate: Number(newPeriod[0]) ? newPeriod[0] : null,
            endDate: Number(newPeriod[1]) ? newPeriod[1] : null,
        })
    };

    const handleSelectedTypeChange = (issueType: IssueDocumentCode) => {
        setFieldValue('issueType', issueType);
    };

    return (
        <>

            {currentWindow === CurrentWindow.Options &&
                <>
                    <Grid
                        container
                        mb={4}
                        justifyContent='space-between'
                        sx={{ marginBottom: 0 }}
                    >
                        <TitlePageH1>
                            {title}
                        </TitlePageH1>
                        <Grid>
                            <LoadingButton
                                variant='contained'
                                color='primary'
                                disabled={!formik.isValid}
                                loading={formik.isSubmitting}
                                sx={css.titleButton}
                                onClick={formik.submitForm}
                            >
                                Запрос отчёта
                            </LoadingButton>
                        </Grid>
                    </Grid>
                    <VerticalTabsCustom
                        isCacheTabs
                        defaultTab={1}
                        height={'calc(100vh - 210px)'}
                        tabsArray={[
                            {   // Выберите параметры - заголовок табов
                                tabButton: {
                                    label:
                                        <TabsTitle
                                            title='Параметры'
                                        />,
                                    otherProps: {
                                        wrapped: true,
                                        disabled: true,
                                        sx: { padding: 0 },
                                    },
                                },
                                tabComponent: <></>,
                            },
                            {   // Общие
                                tabButton: {
                                    label:
                                        <TabLabel
                                            title={'Общие'}
                                            subTitle={
                                                `Период: ${values.startDate && values.endDate
                                                    ? (`${dateTimeHelper.format(values.startDate, 'dd.MM.yy')}`
                                                        + ` — ${dateTimeHelper.format(values.endDate, 'dd.MM.yy')}`)
                                                    : 'период не выбран'
                                                }`
                                            }
                                        />,
                                    otherProps: {
                                        wrapped: true,
                                        sx: { padding: 0 },
                                    },
                                },
                                tabComponent:
                                    <TabComponent title='Общие'>
                                        <>
                                            <Grid
                                                container
                                                sx={{ ...css.contentWrap }}
                                            >
                                                <Typography sx={css.title}>
                                                    Период
                                                </Typography>
                                                <Grid>
                                                    <LocalizationProvider
                                                        dateAdapter={AdapterDateFns}
                                                        localeText={{
                                                            ...ruRU.components.MuiLocalizationProvider.defaultProps.localeText,
                                                            start: 'с',
                                                            end: 'по',
                                                            dateRangePickerToolbarTitle: 'Выберите период',
                                                            cancelButtonLabel: 'Отмена',
                                                        }}
                                                        adapterLocale={ruLocale}
                                                    >
                                                        <DateRangePicker
                                                            value={[values.startDate, values.endDate]}
                                                            onChange={newPeriod => { handleChangeDateRange(newPeriod) }}
                                                            minDate={minStartDate}
                                                            maxDate={new Date()}
                                                            slotProps={{
                                                                textField: {
                                                                    error: !!errors.startDate || !!errors.endDate,
                                                                    size: 'small',
                                                                    onBlur: formik.handleBlur,
                                                                },
                                                            }}
                                                            sx={css.dateRangePickerStyle}
                                                        />
                                                    </LocalizationProvider>
                                                    {(!!errors.startDate && !errors.endDate) &&
                                                        <Typography color='error' sx={css.errorMessageStyle}>
                                                            Дата начала периода не определена
                                                        </Typography>
                                                    }
                                                    {(!!errors.endDate && !errors.startDate) &&
                                                        <Typography color='error' sx={css.errorMessageStyle}>
                                                            Дата конца периода не определена
                                                        </Typography>
                                                    }
                                                    {(!!errors.endDate && !!errors.startDate) &&
                                                        <Typography color='error' sx={css.errorMessageStyle}>
                                                            Некорректный период
                                                        </Typography>
                                                    }
                                                </Grid>
                                            </Grid>
                                            <Grid
                                                container
                                                sx={{ ...css.contentWrap }}
                                            >
                                                <Typography sx={css.title}>
                                                    Тип заявок
                                                </Typography>
                                                <Grid container sx={css.dateRangerGrid}>
                                                    <IssueTypeSelect
                                                        selectedType={values.issueType}
                                                        handleSelectedTypeChange={(issueType) => handleSelectedTypeChange(issueType)}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </>
                                    </TabComponent>,
                            },
                            {   // Цех и участок
                                tabButton: {
                                    label: <SelectDepartmentVerticalTab.TabLabel {...departmentProps}
                                        selectedDepartmentIds={values.departmentIds}
                                    />,
                                    otherProps: {
                                        wrapped: true,
                                        sx: { padding: 0 },
                                    },
                                },
                                tabComponent: (
                                    <SelectDepartmentVerticalTab.TabContent {...departmentProps}
                                        selectedDepartmentIds={values.departmentIds}
                                        onChange={(departmentIds) => setFieldValue('departmentIds', departmentIds)}
                                    />
                                ),
                            },
                        ]
                        }
                    />
                </>
            }

            {/* График */}
            {currentWindow === CurrentWindow.Chart &&
                <Box sx={css.tableWrap}>
                    <IssueStatisticOnWorkersReportTable
                        reportData={reportData}
                        onOptionsButtonClick={() => setCurrentWindow(CurrentWindow.Options)}
                    />
                </Box>
            }

            {/* Заглушка на случай, если нет данных для формирования отчета. */}
            {currentWindow === CurrentWindow.NotData &&
                <NotDataForReportPage onReturnButtonClick={() => setCurrentWindow(CurrentWindow.Options)} />
            }
        </>
    );
}
